import { Switch } from '@halo/ui-react';
import { Box, Flex } from '@chakra-ui/react';
import { Card, ConfirmDialog, MuteIcon, tools } from '@halo/ui-chat-react';
import { useAppStore, type Session } from '@halo/chat-sdk-react';
import { DefaultCollapseGroupId, isSessionCategory, SessionCategoryPrefix } from '@halo/chat-sdk';

export interface MuteNotificationsCardPlusProps {
  session: Session | null;
}
export const MuteNotificationsCardPlus = memo(function MuteNotificationsCardPlus({
  session,
}: MuteNotificationsCardPlusProps) {
  const { t } = useTranslation();
  const { client, sessionsCollapsed, changePinnedList } = useAppStore();
  const [collapseConfirm, setCollapseConfirm] = useState<{
    open: boolean;
    session: Session | null;
  }>({
    open: false,
    session: null,
  });

  const isGroup = useMemo(() => {
    return isSessionCategory(session?.chat_id, SessionCategoryPrefix.Group);
  }, [session?.chat_id]);

  const { isMuted, isMutedAtAll, isCollapse } = useMemo(() => {
    return {
      isMuted: session?.operateAccess?.MUTED,
      isMutedAtAll: session?.operateAccess?.MUTED_AT_ALL,
      isCollapse: !!session?.collapse_group_id,
    };
  }, [session]);

  const onMutedChange = useCallback(
    async (checked: boolean) => {
      try {
        if (!checked) {
          await client?.Api.session.setSessionCollapse(session!.chat_id, '');
          isGroup && (await client?.Api.session.setSessionAtAllMuted(session!.chat_id, false));
        }
        await client?.Api.session.setSessionMuted(session!.chat_id!, checked);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        tools.toaster.error({
          title: 'error',
        });
      }
    },
    [client?.Api.session, session, isGroup]
  );

  const onMutedAtAllChange = useCallback(
    async (checked: boolean) => {
      try {
        await client?.Api.session.setSessionAtAllMuted(session!.chat_id!, checked);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        tools.toaster.error({
          title: 'error',
        });
      }
    },
    [client?.Api.session, session]
  );

  const onCollapseChange = useCallback(
    async (checked: boolean) => {
      if (checked && !sessionsCollapsed.length) {
        setCollapseConfirm({ open: true, session });
        return;
      }
      try {
        if (checked) {
          await changePinnedList(session!.chat_id!, false, false);
        }
        await client?.Api.session.setSessionCollapse(
          session!.chat_id!,
          checked ? DefaultCollapseGroupId : ''
        );
        tools.toaster.success({
          title: checked ? t('chatSettings.chatCollapsed') : t('chatSettings.chatExpanded'),
        });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        tools.toaster.error({
          title: 'error',
        });
      }
    },
    [changePinnedList, client?.Api.session, session, sessionsCollapsed.length, t]
  );

  return (
    <Card>
      <Flex gap="2.5">
        <Box fontSize="4xl">
          <MuteIcon />
        </Box>
        <Flex flex={1}>
          <Flex flexDirection="column" flex={1}>
            <Flex
              flex={1}
              alignItems="center"
              gap="2.5"
              pb="2.5"
              borderBottom="1px solid"
              borderColor="grey.10"
            >
              <Box flex={1} fontSize="sm" fontWeight="medium">
                {t('chatSettings.muteNotifications')}
              </Box>
              <Box>
                <Switch checked={isMuted} onCheckedChange={onMutedChange} />
              </Box>
            </Flex>
            {isMuted && (
              <Box>
                {isGroup && (
                  <Flex
                    alignItems="center"
                    gap="2.5"
                    py="2.5"
                    borderBottom="1px solid"
                    borderColor="grey.10"
                  >
                    <Box flex={1} fontSize="sm" fontWeight="medium">
                      {t('chatSettings.muteAllNotification')}
                    </Box>
                    <Box>
                      <Switch checked={isMutedAtAll} onCheckedChange={onMutedAtAllChange} />
                    </Box>
                  </Flex>
                )}
                <Flex
                  alignItems="center"
                  gap="2.5"
                  py="2.5"
                  borderBottom="1px solid"
                  borderColor="grey.10"
                >
                  <Box flex={1} fontSize="sm" fontWeight="medium">
                    {t('chatSettings.collapseChat')}
                  </Box>
                  <Box>
                    <Switch checked={isCollapse} onCheckedChange={onCollapseChange} />
                  </Box>
                </Flex>
              </Box>
            )}
          </Flex>
        </Flex>
      </Flex>
      <ConfirmDialog
        disabledPortal={false}
        open={collapseConfirm.open}
        onClose={() => setCollapseConfirm({ open: false, session: null })}
        title={t('chatSettings.moveToCollapsedChats')}
        subTitle={t('chatSettings.moveToCollapsedChatsSubTitle')}
        onOk={async () => {
          if (collapseConfirm.session) {
            try {
              await changePinnedList(session!.chat_id!, false, false);
              await client?.Api.session.setSessionCollapse(
                collapseConfirm.session.chat_id,
                'default_collapse_group'
              );
              setCollapseConfirm({ open: false, session: null });
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (error) {
              tools.toaster.error({
                title: 'error',
              });
            }
          }
        }}
        okText={t('buttons.confirm')}
      />
    </Card>
  );
});
